import { useEffect, useMemo, useState } from "react";
import { CalendarIcon, getRoundStrategyTitle, getLocalTime, formatLocalDateAsISOString, getTokensByChainId, stringToBlobUrl, getChainById } from "common";
import { Input } from "common/src/styles";
import AlloV1 from "common/src/icons/AlloV1";
import AlloV2 from "common/src/icons/AlloV2";
import { ReactComponent as Search } from "../../../assets/search-grey.svg";
import { getDaysLeft, isDirectRound, isInfiniteDate } from "../../api/utils";
import { PassportWidget } from "../../common/PassportWidget";
import RoundEndedBanner from "../../common/RoundEndedBanner";
import { Badge } from "../../common/styles";
import Breadcrumb from "../../common/Breadcrumb";
const builderURL = process.env.REACT_APP_BUILDER_URL;
import CartNotification from "../../common/CartNotification";
import { useAccount, useToken } from "wagmi";
import { getAddress } from "viem";
import { RoundViewLayout } from "../../common/DefaultLayout";
import { getUnixTime } from "date-fns";
import { PresentationChartBarIcon } from "@heroicons/react/24/outline";
import RoundStartCountdownBadge from "../RoundStartCountdownBadge";
import ApplicationsCountdownBanner from "../ApplicationsCountdownBanner";
import { ProjectList } from "./ProjectList";
import { RoundStatsTabContent } from "./RoundStatsTabContent";
import { RoundTabs } from "./RoundTabs";
import { getAlloVersion } from "common/src/config";
import { StakingBannerAndModal } from "../ViewProjectDetails/components/StakingBannerAndModal";
import { useIsStakable } from "../ViewProjectDetails/components/StakingBannerAndModal/hooks/useIsStakable";
import { SortDropdown } from "./SortDropdown";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const alloVersion = getAlloVersion();
export function RoundPage(props) {
  var _round$roundMetadata, _round$roundMetadata2, _nativePayoutToken$co, _round$roundMetadata3, _round$roundMetadata4, _round$payoutStrategy, _round$payoutStrategy2, _round$roundMetadata5, _tokenData$symbol, _round$roundMetadata6;
  const {
    round,
    chainId,
    roundId
  } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [projects, setProjects] = useState();
  const [randomizedProjects, setRandomizedProjects] = useState();
  const {
    address: walletAddress
  } = useAccount();
  const isStakableRound = useIsStakable({
    chainId: Number(chainId),
    roundId
  });
  const isSybilDefenseEnabled = (round === null || round === void 0 || (_round$roundMetadata = round.roundMetadata) === null || _round$roundMetadata === void 0 || (_round$roundMetadata = _round$roundMetadata.quadraticFundingConfig) === null || _round$roundMetadata === void 0 ? void 0 : _round$roundMetadata.sybilDefense) === true || (round === null || round === void 0 || (_round$roundMetadata2 = round.roundMetadata) === null || _round$roundMetadata2 === void 0 || (_round$roundMetadata2 = _round$roundMetadata2.quadraticFundingConfig) === null || _round$roundMetadata2 === void 0 ? void 0 : _round$roundMetadata2.sybilDefense) !== "none";
  const [showCartNotification, setShowCartNotification] = useState(false);
  const [currentProjectAddedToCart, setCurrentProjectAddedToCart] = useState({});
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const disableAddToCartButton = alloVersion === "allo-v2" && roundId.startsWith("0x") || props.isAfterRoundEndDate;
  const showProjectCardFooter = !isDirectRound(round) && props.isAfterRoundStartDate;
  useEffect(() => {
    if (showCartNotification) {
      setTimeout(() => {
        setShowCartNotification(false);
      }, 3000);
    }
  }, [showCartNotification]);
  const renderCartNotification = () => {
    return /*#__PURE__*/_jsx(CartNotification, {
      showCartNotification: showCartNotification,
      setShowCartNotification: setShowCartNotification,
      currentProjectAddedToCart: currentProjectAddedToCart
    });
  };
  useEffect(() => {
    var _projects;
    let projects = round === null || round === void 0 ? void 0 : round.approvedProjects;

    // shuffle projects
    projects = (_projects = projects) === null || _projects === void 0 ? void 0 : _projects.sort(() => Math.random() - 0.5);
    setRandomizedProjects(projects);
    setProjects(projects);
    setIsProjectsLoading(false);
  }, [round]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (searchQuery) {
      const timeOutId = setTimeout(() => filterProjectsByTitle(searchQuery), 300);
      return () => clearTimeout(timeOutId);
    } else {
      setProjects(randomizedProjects);
      setIsProjectsLoading(false);
    }
  });
  const filterProjectsByTitle = query => {
    // filter by exact title matches first
    // e.g if searchString is "ether" then "ether grant" comes before "ethereum grant"
    const projects = round === null || round === void 0 ? void 0 : round.approvedProjects;
    const exactMatches = projects === null || projects === void 0 ? void 0 : projects.filter(project => project.projectMetadata.title.toLocaleLowerCase() === query.toLocaleLowerCase());
    const nonExactMatches = projects === null || projects === void 0 ? void 0 : projects.filter(project => project.projectMetadata.title.toLocaleLowerCase().includes(query.toLocaleLowerCase()) && project.projectMetadata.title.toLocaleLowerCase() !== query.toLocaleLowerCase());
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    setProjects([...exactMatches, ...nonExactMatches]);
    setIsProjectsLoading(false);
  };
  const {
    data
  } = useToken({
    address: getAddress(props.round.token),
    chainId: Number(props.chainId)
  });
  const nativePayoutToken = getTokensByChainId(props.chainId).find(t => t.address === getAddress(props.round.token));
  const tokenData = data !== null && data !== void 0 ? data : {
    ...nativePayoutToken,
    symbol: (_nativePayoutToken$co = nativePayoutToken === null || nativePayoutToken === void 0 ? void 0 : nativePayoutToken.code) !== null && _nativePayoutToken$co !== void 0 ? _nativePayoutToken$co : "ETH"
  };
  const breadCrumbs = [{
    name: "Explorer Home",
    path: "/"
  }, {
    name: (_round$roundMetadata3 = round.roundMetadata) === null || _round$roundMetadata3 === void 0 ? void 0 : _round$roundMetadata3.name,
    path: `/round/${chainId}/${roundId}`
  }];
  const applicationURL = `${builderURL}/#/chains/${chainId}/rounds/${roundId}`;
  const currentTime = new Date();
  const isBeforeApplicationEndDate = round && (isInfiniteDate(round.applicationsEndTime) || round.applicationsEndTime >= currentTime);
  const isAlloV1 = roundId.startsWith("0x");
  const getRoundEndsText = () => {
    if (!round.roundEndTime) return;
    const roundEndsIn = round.roundEndTime === undefined ? undefined : getDaysLeft(getUnixTime(round.roundEndTime).toString());
    if (roundEndsIn === undefined || roundEndsIn < 0) return;
    if (roundEndsIn === 0) return "Ends today";
    return `${roundEndsIn} ${roundEndsIn === 1 ? "day" : "days"} left`;
  };
  const roundEndsText = getRoundEndsText();
  const handleTabChange = tabIndex => {
    setSelectedTab(tabIndex);
  };
  const projectDetailsTabs = useMemo(() => {
    var _projects$length;
    const projectsTab = {
      name: isDirectRound(round) ? "Approved Projects" : `All Projects (${(_projects$length = projects === null || projects === void 0 ? void 0 : projects.length) !== null && _projects$length !== void 0 ? _projects$length : 0})`,
      content: /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx(ProjectList, {
          projects: projects,
          roundRoutePath: `/round/${chainId}/${roundId}`,
          showProjectCardFooter: showProjectCardFooter,
          isBeforeRoundEndDate: !disableAddToCartButton,
          roundId: roundId,
          isProjectsLoading: isProjectsLoading,
          round: round,
          chainId: chainId,
          setCurrentProjectAddedToCart: setCurrentProjectAddedToCart,
          setShowCartNotification: setShowCartNotification
        })
      })
    };
    const statsTab = {
      name: props.isBeforeRoundEndDate ? "Stats" : "Results",
      icon: PresentationChartBarIcon,
      content: /*#__PURE__*/_jsx(_Fragment, {
        children: /*#__PURE__*/_jsx(RoundStatsTabContent, {
          roundId: roundId,
          round: round,
          chainId: chainId,
          token: nativePayoutToken,
          tokenSymbol: tokenData.symbol
        })
      })
    };
    return [projectsTab, statsTab];
  }, [projects, round, props.isBeforeRoundEndDate, chainId, disableAddToCartButton, isProjectsLoading, nativePayoutToken, roundId, tokenData.symbol, showProjectCardFooter]);
  const roundStart = isDirectRound(round) ? round.applicationsStartTime : round.roundStartTime;
  const roundEnd = isDirectRound(round) ? round.applicationsEndTime : round.roundEndTime;
  const chain = getChainById(chainId);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(RoundViewLayout, {
      infoCard: /*#__PURE__*/_jsx(StakingBannerAndModal, {
        isRoundView: true
      }),
      children: [showCartNotification && renderCartNotification(), props.isAfterRoundEndDate && /*#__PURE__*/_jsx("div", {
        className: "relative top-6",
        children: /*#__PURE__*/_jsx(RoundEndedBanner, {})
      }), /*#__PURE__*/_jsxs("div", {
        className: "flex flex-row justify-between mb-2 mt-8",
        children: [/*#__PURE__*/_jsx("div", {
          className: "flex flex-col pt-2 justify-center",
          "data-testid": "bread-crumbs",
          children: /*#__PURE__*/_jsx(Breadcrumb, {
            items: breadCrumbs
          })
        }), walletAddress && isSybilDefenseEnabled && /*#__PURE__*/_jsx("div", {
          "data-testid": "passport-widget",
          children: /*#__PURE__*/_jsx(PassportWidget, {
            round: round,
            alignment: "right"
          })
        })]
      }), /*#__PURE__*/_jsxs("section", {
        children: [/*#__PURE__*/_jsxs("div", {
          className: "flex flex-col md:items-center md:justify-between md:gap-8 md:flex-row md:mb-0 mb-4",
          children: [/*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsxs("div", {
              className: "pb-4",
              children: [isAlloV1 && /*#__PURE__*/_jsx(AlloV1, {
                color: "black"
              }), !isAlloV1 && /*#__PURE__*/_jsx(AlloV2, {
                color: "black"
              })]
            }), /*#__PURE__*/_jsxs("div", {
              className: "flex items-center gap-4 mb-4",
              children: [/*#__PURE__*/_jsx("h1", {
                "data-testid": "round-title",
                className: "text-2xl sm:text-3xl font-modern-era-medium text-grey-500",
                children: (_round$roundMetadata4 = round.roundMetadata) === null || _round$roundMetadata4 === void 0 ? void 0 : _round$roundMetadata4.name
              }), props.isBeforeRoundStartDate ? /*#__PURE__*/_jsx(RoundStartCountdownBadge, {
                targetDate: roundStart
              }) : !props.isAfterRoundEndDate ? /*#__PURE__*/_jsx(Badge, {
                color: "blue",
                rounded: "full",
                className: "flex-shrink-0 px-2.5 font-modern-era-bold",
                children: roundEndsText
              }) : /*#__PURE__*/_jsx(Badge, {
                color: "orange",
                rounded: "full",
                className: "flex-shrink-0 px-2.5",
                children: "Round ended"
              })]
            }), /*#__PURE__*/_jsx(Badge, {
              color: "grey",
              rounded: "full",
              "data-testid": "round-badge",
              className: " text-gray-900 inline-flex px-2.5 mb-4",
              children: /*#__PURE__*/_jsx("span", {
                children: ((_round$payoutStrategy = round.payoutStrategy) === null || _round$payoutStrategy === void 0 ? void 0 : _round$payoutStrategy.strategyName) && getRoundStrategyTitle((_round$payoutStrategy2 = round.payoutStrategy) === null || _round$payoutStrategy2 === void 0 ? void 0 : _round$payoutStrategy2.strategyName)
              })
            }), /*#__PURE__*/_jsxs("div", {
              className: "text-grey-400 flex gap-2 mb-2",
              children: [/*#__PURE__*/_jsx("span", {
                children: "on"
              }), /*#__PURE__*/_jsxs("div", {
                className: "flex items-center",
                children: [/*#__PURE__*/_jsx("img", {
                  className: "w-4 h-4 mt-0.5 mr-1",
                  src: stringToBlobUrl(chain.icon),
                  alt: "Round Chain Logo"
                }), /*#__PURE__*/_jsx("span", {
                  children: chain.prettyName
                })]
              })]
            }), /*#__PURE__*/_jsxs("div", {
              className: "flex flex-col gap-2 text-grey-500 mb-4",
              children: [isBeforeApplicationEndDate && /*#__PURE__*/_jsxs("p", {
                "data-testid": "application-period",
                className: "mr-4 flex items-center",
                children: [/*#__PURE__*/_jsx("span", {
                  className: "mr-2",
                  children: "Apply"
                }), /*#__PURE__*/_jsx(CalendarIcon, {
                  className: "w-4 h-4 !text-grey-400 inline-block mr-2"
                }), /*#__PURE__*/_jsxs("span", {
                  children: [/*#__PURE__*/_jsxs("span", {
                    className: "px-2 rounded bg-grey-50",
                    children: [/*#__PURE__*/_jsx("span", {
                      className: "mr-1",
                      children: formatLocalDateAsISOString(round.applicationsStartTime)
                    }), /*#__PURE__*/_jsx("span", {
                      children: getLocalTime(round.applicationsStartTime)
                    })]
                  }), /*#__PURE__*/_jsx("span", {
                    className: "px-1.5",
                    children: "-"
                  }), /*#__PURE__*/_jsx("span", {
                    className: "px-2 rounded bg-grey-50",
                    children: !isInfiniteDate(roundEnd) ? /*#__PURE__*/_jsxs(_Fragment, {
                      children: [/*#__PURE__*/_jsx("span", {
                        className: "mr-1",
                        children: formatLocalDateAsISOString(round.applicationsEndTime)
                      }), /*#__PURE__*/_jsx("span", {
                        children: getLocalTime(roundEnd)
                      })]
                    }) : /*#__PURE__*/_jsx("span", {
                      children: "No End Date"
                    })
                  })]
                })]
              }), !isDirectRound(round) && /*#__PURE__*/_jsxs("p", {
                "data-testid": "round-period",
                className: "mr-4 flex items-center",
                children: [/*#__PURE__*/_jsx("span", {
                  className: "mr-2",
                  children: "Donate"
                }), /*#__PURE__*/_jsx(CalendarIcon, {
                  className: "w-4 h-4 !text-grey-400 inline-block mr-2"
                }), /*#__PURE__*/_jsxs("span", {
                  children: [/*#__PURE__*/_jsxs("span", {
                    className: "px-2 rounded bg-grey-50",
                    children: [/*#__PURE__*/_jsx("span", {
                      className: "mr-1",
                      children: formatLocalDateAsISOString(roundStart)
                    }), /*#__PURE__*/_jsx("span", {
                      children: getLocalTime(roundStart)
                    })]
                  }), /*#__PURE__*/_jsx("span", {
                    className: "px-1.5",
                    children: "-"
                  }), /*#__PURE__*/_jsx("span", {
                    className: "px-2 rounded bg-grey-50",
                    children: !isInfiniteDate(roundEnd) ? /*#__PURE__*/_jsxs(_Fragment, {
                      children: [/*#__PURE__*/_jsx("span", {
                        className: "mr-1",
                        children: formatLocalDateAsISOString(roundEnd)
                      }), /*#__PURE__*/_jsx("span", {
                        children: getLocalTime(roundEnd)
                      })]
                    }) : /*#__PURE__*/_jsx("span", {
                      children: "No End Date"
                    })
                  })]
                })]
              })]
            })]
          }), !isDirectRound(round) && /*#__PURE__*/_jsxs("div", {
            "data-testid": "matching-funds",
            className: "bg-grey-50 p-8 rounded-2xl",
            children: [/*#__PURE__*/_jsxs("p", {
              className: "text-3xl mb-2 font-mono tracking-tighter",
              children: [(_round$roundMetadata5 = round.roundMetadata) === null || _round$roundMetadata5 === void 0 || (_round$roundMetadata5 = _round$roundMetadata5.quadraticFundingConfig) === null || _round$roundMetadata5 === void 0 ? void 0 : _round$roundMetadata5.matchingFundsAvailable.toLocaleString(), "\xA0", (_tokenData$symbol = tokenData === null || tokenData === void 0 ? void 0 : tokenData.symbol) !== null && _tokenData$symbol !== void 0 ? _tokenData$symbol : "..."]
            }), /*#__PURE__*/_jsx("p", {
              children: "Matching Pool"
            })]
          })]
        }), /*#__PURE__*/_jsx("p", {
          className: "mb-4 overflow-x-auto",
          children: (_round$roundMetadata6 = round.roundMetadata) === null || _round$roundMetadata6 === void 0 || (_round$roundMetadata6 = _round$roundMetadata6.eligibility) === null || _round$roundMetadata6 === void 0 ? void 0 : _round$roundMetadata6.description
        })]
      }), /*#__PURE__*/_jsx("hr", {
        className: "mt-4 mb-8"
      }), /*#__PURE__*/_jsxs("div", {
        className: "flex flex-col items-center gap-8",
        children: [isBeforeApplicationEndDate && /*#__PURE__*/_jsx(ApplicationsCountdownBanner, {
          startDate: round.applicationsStartTime,
          endDate: round.applicationsEndTime,
          applicationURL: applicationURL
        }), /*#__PURE__*/_jsxs("div", {
          className: "mb-2 flex flex-col lg:flex-row w-full justify-between gap-2",
          children: [/*#__PURE__*/_jsx(RoundTabs, {
            tabs: projectDetailsTabs,
            selected: selectedTab,
            onChange: handleTabChange
          }), selectedTab === 0 && /*#__PURE__*/_jsxs("div", {
            className: "relative flex items-center gap-2",
            children: [/*#__PURE__*/_jsxs("div", {
              className: "relative",
              children: [/*#__PURE__*/_jsx(Search, {
                className: "absolute h-4 w-4 mt-3 ml-3 "
              }), /*#__PURE__*/_jsx(Input, {
                className: "w-full lg:w-64 h-8 rounded-full pl-10 font-mono",
                type: "text",
                placeholder: "Search",
                value: searchQuery,
                onChange: e => setSearchQuery(e.target.value)
              })]
            }), isStakableRound && props.isAfterRoundStartDate && /*#__PURE__*/_jsxs("div", {
              className: "flex gap-2 items-center",
              children: [/*#__PURE__*/_jsx("div", {
                children: "Sort by"
              }), /*#__PURE__*/_jsx(SortDropdown, {})]
            })]
          })]
        }), /*#__PURE__*/_jsx("div", {
          children: projectDetailsTabs[selectedTab].content
        })]
      })]
    })
  });
}